import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { loadStripe, StripeElementsOptions } from '@stripe/stripe-js';
import { Elements, PaymentElement, useStripe, useElements } from '@stripe/react-stripe-js';
import { useLanguage } from '@/contexts/LanguageContext';
import { Navbar } from '@/components/Navbar';
import { Footer } from '@/components/Footer';
import { Button } from '@/components/ui/button';
import { useAuth } from '@/contexts/AuthContext';
import { Card, CardDescription } from '@/components/ui/card';
import { Separator } from '@/components/ui/separator';
import { toast } from '@/components/ui/use-toast';
import { ChevronLeft, CreditCard, Lock, FileText, Loader2 } from 'lucide-react';
import SubscriptionStatus from '@/components/payment/SubscriptionStatus';
import { STRIPE_PUBLISHABLE_KEY, BASE_URL } from '@/config/api';

const stripePromise = loadStripe(STRIPE_PUBLISHABLE_KEY);

const formatDate = (dateString: string) => {
  const { locale } = useLanguage();
  const date = new Date(dateString);
  return new Intl.DateTimeFormat(locale === 'fr' ? 'fr-FR' : 'en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  }).format(date);
};

const PaymentPage = () => {
  const { t } = useLanguage();
  const navigate = useNavigate();
  const location = useLocation();
  const { user, token } = useAuth();
  const { invoice } = location.state || {};
  const [loading, setLoading] = useState(true);
  const [email, setEmail] = useState('');
  const [clientSecret, setClientSecret] = useState<string>('');
  const [subscriptionStatus, setSubscriptionStatus] = useState(null);
  const [subscriptionError, setSubscriptionError] = useState<string | undefined>();
  const [invoiceId, setInvoiceId] = useState<number | null>(null);

  useEffect(() => {
    if (invoiceId) {
      navigate('/invoices/' + invoiceId);
    }
  }, [invoiceId]);

  useEffect(() => {
    const initializePayment = async () => {
      try {
        if (!invoice) {
          throw new Error('Invoice not found');
        }
        const response = await fetch(`${BASE_URL}/api/create-payment-intent`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
          body: JSON.stringify({ amount: invoice.total, currency: invoice.invoice_currency }),
        });
        const data = await response.json();
        if (!response.ok) {
          throw new Error(data.error || 'Failed to initialize payment');
        }
        setClientSecret(data.clientSecret);
        if (user && user.email) {
          setEmail(user.email);
        }
        setLoading(false);
      } catch (error) {
        console.error('Failed to initialize payment:', error);
        setSubscriptionError('Failed to initialize payment. Please try again later.');
        setSubscriptionStatus('incomplete');
        setLoading(false);
      }
    };
    initializePayment();
  }, [invoice, user, token]);

  const options: StripeElementsOptions = {
    clientSecret,
    appearance: {
      theme: 'stripe',
      variables: {
        colorPrimary: '#8B5CF6',
        colorBackground: '#ffffff',
        colorText: '#1A1F2C',
      },
    },
    loader: 'auto',
    paymentMethodCreation: 'manual',
  };

  const handleRetry = () => {
    setSubscriptionStatus(null);
    setSubscriptionError(undefined);
  };

  if (loading) {
    return (
      <div className="min-h-screen flex flex-col">
        <Navbar />
        <div className="flex-1 container mx-auto px-4 py-8 flex items-center justify-center">
          <SubscriptionStatus status="processing" />
        </div>
        <Footer />
      </div>
    );
  }

  if (subscriptionStatus) {
    return (
      <div className="min-h-screen flex flex-col">
        <Navbar />
        <div className="flex-1 container mx-auto px-4 py-8 flex items-center justify-center">
          <SubscriptionStatus
            status={subscriptionStatus}
            errorMessage={subscriptionError}
            onRetry={subscriptionStatus === 'incomplete' ? handleRetry : undefined}
          />
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen flex flex-col">
      <Navbar />
      <main className="flex-1 py-16">
        <div className="container px-4 md:px-6 pt-8">
          <Button variant="ghost" className="mb-8 pl-0" onClick={() => navigate('/invoices')}>
            <ChevronLeft className="mr-2 h-4 w-4" />
            {t('common.back')}
          </Button>
          <Card className="p-6 mx-auto max-w-3xl">
            <h1 className="text-2xl font-bold mb-2">Invoice Payment</h1>
             <CardDescription>Pay your coach {invoice.coach.name} by credit card</CardDescription>
              <p><br></br></p>
            <Card className="mb-6 p-6 bg-primary/5">
              <h4 className="text-md font-medium mb-4">{invoice.name}</h4>
              <p className="text-sm text-muted-foreground">
                {t('payment.invoice')}: {formatDate(invoice.date)}
              </p>
              <div className="flex justify-between font-medium text-lg mt-4 text-xs">
                <span>Subtotal</span>
                <span>{invoice.total} {invoice.invoice_currency}</span>
              </div>
              <div className="flex justify-between font-bold text-xl mt-2">
                <span>Total</span>
                <span>{invoice.total} {invoice.invoice_currency}</span>
              </div>
            </Card>

            <div>
              <h2 className="text-xl font-medium mb-4 flex items-center">
                <CreditCard className="mr-2 h-5 w-5 text-muted-foreground" />
                {t('payment.payment_details')}
              </h2>
              {clientSecret ? (
                <Elements stripe={stripePromise} options={options}>
                  <SubscriptionCheckoutForm
                    email={email}
                    clientSecret={clientSecret}
                    setInvoiceId={setInvoiceId}
                    invoice={invoice}
                    onSubscriptionSuccess={() => setSubscriptionStatus('active')}
                    onSubscriptionError={(error) => {
                      setSubscriptionError(error);
                      setSubscriptionStatus('incomplete');
                    }}
                    onRequiresAction={() => setSubscriptionStatus('requires_action')}
                  />
                </Elements>
              ) : (
                <div className="p-6">
                  <p>Loading payment form...</p>
                </div>
              )}
            </div>
            <div className="p-4 bg-gray-50 border-t flex items-center justify-center text-sm text-muted-foreground mt-6">
              <Lock className="h-4 w-4 mr-2" />
              {t('payment.secure_encryption')}
            </div>
          </Card>
        </div>
      </main>
      <Footer />
    </div>
  );
};

function SubscriptionCheckoutForm({
  email,
  clientSecret,
  setInvoiceId,
  invoice,
  onSubscriptionSuccess,
  onSubscriptionError,
  onRequiresAction
}) {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { t } = useLanguage();
  const stripe = useStripe();
  const elements = useElements();

  const handleSubmit = async () => {
    if (!stripe || !elements) {
      return;
    }
    setIsSubmitting(true);
    try {
      const { error: submitError } = await elements.submit();
      if (submitError) {
        onSubscriptionError(submitError.message || 'An unexpected error occurred');
        console.error('[Submit Error]', submitError);
        setIsSubmitting(false);
        return;
      }
      const { error, paymentMethod } = await stripe.createPaymentMethod({
        elements,
        params: {
          billing_details: {
            email: email,
          },
        },
      });
      if (error) {
        onSubscriptionError(error.message || 'An unexpected error occurred');
        console.error('[Subscription Error]', error);
      } else {
        const token = localStorage.getItem('auth-token');
        if (!token) {
          throw new Error('Not authenticated');
        }
        const response = await fetch(`${BASE_URL}/api/invoice/payment`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          },
          body: JSON.stringify({
            paymentMethodId: paymentMethod.id,
            invoiceId: invoice.id,
          })
        });
        const data = await response.json();
        if (!response.ok) {
          throw new Error(data.message || 'Failed to create subscription');
        }
        if (data.status === "requires_action") {
          const { error: confirmError } = await stripe.confirmCardPayment(data.clientSecret);
          if (confirmError) {
            onSubscriptionError(confirmError.message || 'An unexpected error occurred');
          } else {
            setInvoiceId(invoice.id);
            toast({
              title: t('payment.payment_successful'),
              description: t('payment.success_description'),
            });
          }
        } else {
          setInvoiceId(invoice.id);
          toast({
            title: t('payment.payment_successful'),
            description: t('payment.success_description'),
          });
        }
      }
    } catch (e) {
      console.error('Subscription submission error:', e);
      onSubscriptionError(t('subscription.network_error'));
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div>
      <PaymentElement />
      <button
        type="button"
        className="mt-4 w-full bg-indigo-600 text-white py-2 px-4 rounded-md hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
        onClick={handleSubmit}
        disabled={isSubmitting}
      >
        {isSubmitting ? (
          <>
            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
            {t('payment.processing')}
          </>
        ) : (
          t('payment.complete_payment')
        )}
      </button>
    </div>
  );
}

export default PaymentPage;
